import { useLocalStorage } from 'usehooks-ts';
import { FormProvider, useForm } from 'react-hook-form';
import { Suspense, useState } from 'react';
import React from 'react';
import { CompleteStoreFragment } from 'drycut-graphql';
import { Loader } from 'ui-components';
import { BookingProvider } from './BookingProvider';
import { Navigation } from 'navigation';
import { NameStep, PaymentStep, PhoneStep } from './Steps';
import { BookingUnavailable } from '../BookingUnavailable';
import { DateTime } from 'luxon';

type Props = {
  store?: CompleteStoreFragment;
  manual?: boolean;
};

export type BookFormValues = {
  phone: string;
  name: string;
  existingCustomer: boolean;
  kidCustomer: boolean;
  silentBooking: boolean;
  couponCode: string;
};

const defaultValues: BookFormValues = {
  phone: '',
  name: '',
  existingCustomer: false,
  kidCustomer: false,
  silentBooking: false,
  couponCode: '',
};

export const PHONE_STORAGE_KEY = 'DRYCUT-StoredCustomerPhoneNumber';

export type BookingStep = 'phone' | 'name' | 'payment';

export function BookingStepper({ store, manual = false }: Props): React.ReactElement {
  const [storedNumber, setStoredNumber] = useLocalStorage<string>(PHONE_STORAGE_KEY, '');
  const [currentStep, setCurrentStep] = useState<BookingStep>('phone');
  const ready = store;

  const methods = useForm<BookFormValues>({
    defaultValues: {
      ...defaultValues,
      phone: manual ? '' : storedNumber,
    },
  });

  // When the phone step is submitted we need to check do a customer validation on the server. If
  // the customer is valid then we set the existing customer variable. If the customer is not valid
  // then we need to present the error.
  const handlePhoneComplete = () => {
    setStoredNumber(methods.getValues('phone'));

    setCurrentStep('name');
  };

  const handleNameComplete = () => {
    setCurrentStep('payment');
  };

  const handleCancel = () => {
    methods.reset();

    setCurrentStep('phone');
  };

  const handleCancelFirstStep = () => {
    methods.reset();

    window.location.href = Navigation.HOME_URL;
  };

  if (!ready) {
    return <Loader />;
  }

  // If there is no availability for today, we cannot proceed with the booking.
  if (
    manual &&
    ready &&
    !DateTime.fromISO(store.nextAvailableTime).hasSame(DateTime.now(), 'day')
  ) {
    return <BookingUnavailable store={store} />;
  }

  return (
    <FormProvider {...methods}>
      <BookingProvider manual={manual}>
        <Suspense fallback={<Loader />}>
          {currentStep === 'phone' && (
            <PhoneStep
              store={store}
              onNext={handlePhoneComplete}
              onCancel={handleCancelFirstStep}
            />
          )}
          {currentStep === 'name' && (
            <NameStep
              store={store}
              onNext={handleNameComplete}
              onPrevious={() => setCurrentStep('phone')}
              onCancel={handleCancel}
            />
          )}
          {currentStep === 'payment' && <PaymentStep store={store} onCancel={handleCancel} />}
        </Suspense>
      </BookingProvider>
    </FormProvider>
  );
}
